import { useState, useEffect, useContext, createContext } from 'react'

const AdminAuthContext = createContext(null)

export const AdminAuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [adminData, setAdminData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession()
  }, [])

  const checkExistingSession = async () => {
    try {
      const token = localStorage.getItem('adminToken')
      if (!token) {
        setIsLoading(false)
        return
      }

      const response = await fetch('/api/admin/validate', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const data = await response.json()
        setIsAuthenticated(true)
        setAdminData(data.data)
      } else {
        localStorage.removeItem('adminToken')
      }
    } catch (error) {
      console.error('Error checking session:', error)
      localStorage.removeItem('adminToken')
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        // Validate response data structure
        if (!data.data || !data.data.token || !data.data.email || !data.data.role) {
          console.error('Invalid login response structure:', data)
          setError('Réponse du serveur invalide')
          return false
        }

        // Store token and set authenticated state
        localStorage.setItem('adminToken', data.data.token)
        setIsAuthenticated(true)
        setAdminData(data.data)
        return true
      } else {
        setError(data.error || 'Identifiants invalides')
        return false
      }
    } catch (error) {
      console.error('Login error:', error)
      setError('Erreur de connexion au serveur')
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      const token = localStorage.getItem('adminToken')
      if (token) {
        await fetch('/api/admin/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      localStorage.removeItem('adminToken')
      setIsAuthenticated(false)
      setAdminData(null)
    }
  }

  // Role checking helpers
  const hasRole = (role) => {
    return adminData?.role === role
  }

  const hasAnyRole = (roles) => {
    return roles.includes(adminData?.role)
  }

  const isAdmin = () => hasRole('admin')
  const isEditor = () => hasAnyRole(['editor', 'admin'])
  const isReader = () => hasAnyRole(['reader', 'editor', 'admin'])

  const value = {
    isAuthenticated,
    adminData,
    isLoading,
    error,
    login,
    logout,
    hasRole,
    hasAnyRole,
    isAdmin,
    isEditor,
    isReader
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext)
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider')
  }
  return context
}
